import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getJson } from '@/lib/api';

/**
 * bcecloud's view of the Salesforce record a widget is embedded on. Mirrors the
 * `CanvasContext` union returned by `GET /api/v3/canvas/context`.
 */
/** One of an event's contracts, as the embed shows it. */
export interface CanvasContractSummary {
  readonly id: string;
  readonly name: string;
  readonly status: string;
  readonly contractNumber: string | null;
}

export type CanvasContext =
  | {
      readonly kind: 'opportunity';
      readonly externalId: string;
      readonly event: {
        readonly id: string;
        readonly name: string | null;
        readonly contractNumber: string | null;
        readonly eventDate: string | null;
        readonly accountName: string | null;
      } | null;
      readonly contracts: readonly CanvasContractSummary[];
    }
  | { readonly kind: 'account'; readonly externalId: string; readonly account: null }
  | { readonly kind: 'contact'; readonly externalId: string; readonly contact: null }
  | { readonly kind: 'unknown'; readonly externalId: string };

/**
 * Fetches bcecloud's context for the embedded record. Authenticated by the
 * session cookie the `/canvas` SSO handler set — the embed never holds a Clerk
 * session, so there is no bearer token here.
 *
 * @param sObjectType - The Salesforce SObject API name.
 * @param externalId - The Salesforce record id.
 * @returns The React Query result for the record context.
 */
export function useCanvasContext(
  sObjectType: string,
  externalId: string,
): UseQueryResult<CanvasContext> {
  return useQuery({
    queryKey: ['canvas-context', sObjectType, externalId],
    queryFn: ({ signal }) =>
      getJson<CanvasContext>('/canvas/context', { sObjectType, externalId }, signal),
    enabled: externalId.length > 0 && sObjectType.length > 0,
  });
}
